import type { EvidenceStatus } from '../coverage.js';
import { compareCodes, plural } from '../util.js';
import {
  knowledgeCitations,
  knowledgeNumber,
  markAssumptions,
  type FutureMapCheck,
} from './future.js';
import {
  KEY_PATTERN,
  MAX_LABEL_LENGTH,
  MAX_NAME_LENGTH,
  citedId,
  cleanText,
  cloneModel,
  isStepType,
  isTaskType,
  nextFreeKey,
  quote,
  stepLabel,
  stepsByKey,
  type KnowledgeItemKind,
  type KnowledgeItemStatus,
  type MapFlow,
  type MapLane,
  type MapModel,
  type MapStep,
  type StepType,
} from './model.js';
import { mapIssues, type MapCheck, type MapIssue } from './validate.js';

/**
 * Typed change sets (ADR-017 §4, E12-F09 §2 and §3.2). Every change to a map,
 * from chat, the canvas or the side panel, is a list of operations applied in
 * order to a copy of the base version. The whole set is refused, naming the
 * operation and the check, if any operation or the map it leaves fails a
 * check (Decision 54.29). Applying is the only way a map changes (rule 27).
 *
 * Future-state maps (E13, ADR-020 §4) add three operations, refused on a
 * current map: `link_vendor_knowledge`, `unlink_vendor_knowledge` and
 * `set_assumption`.
 */

export const CHANGE_OPERATIONS = [
  'add_step',
  'move_step',
  'remove_step',
  'rename_step',
  'add_lane',
  'rename_lane',
  'remove_lane',
  'add_flow',
  'remove_flow',
  'relabel_flow',
  'link_evidence',
  'unlink_evidence',
  'link_pain_point',
  'unlink_pain_point',
  'link_vendor_knowledge',
  'unlink_vendor_knowledge',
  'set_assumption',
] as const;
export type ChangeOperationKind = (typeof CHANGE_OPERATIONS)[number];

/** The ten operations map chat may propose. */
export const CHAT_OPERATIONS: readonly ChangeOperationKind[] = CHANGE_OPERATIONS.slice(0, 10);

/** Valid on future-state maps only (ADR-020 §4). Chat may propose them there. */
export const FUTURE_OPERATIONS: readonly ChangeOperationKind[] = [
  'link_vendor_knowledge',
  'unlink_vendor_knowledge',
  'set_assumption',
];

/** What map chat may propose on a future-state map: the ten, and the three future operations. */
export const FUTURE_CHAT_OPERATIONS: readonly ChangeOperationKind[] = [
  ...CHAT_OPERATIONS,
  ...FUTURE_OPERATIONS,
];

/**
 * Evidence and pain points are linked and unlinked in the step panel only;
 * map chat is never offered these (an AI draft links them through its own
 * tie-back, `tieBackMapDraft`).
 */
export const MANUAL_ONLY_OPERATIONS: readonly ChangeOperationKind[] = [
  'link_evidence',
  'unlink_evidence',
  'link_pain_point',
  'unlink_pain_point',
];

export const MAX_OPERATIONS = 200;

export interface AddStepOperation {
  op: 'add_step';
  /** Assigned by code when absent (`s1`, `s2`, …), so later operations in the set can name it. */
  key?: string;
  type: StepType;
  lane: string;
  name: string;
  system?: string | null;
  /** Insert the step on the single flow leaving this step. Without it the step starts unconnected. */
  after_step?: string;
}

/** A change of lane, of order, or both. Order changes re-wire flows (see `applyChangeSet`). */
export interface MoveStepOperation {
  op: 'move_step';
  step: string;
  lane?: string;
  after_step?: string;
}

export interface RemoveStepOperation {
  op: 'remove_step';
  step: string;
}

export interface RenameStepOperation {
  op: 'rename_step';
  step: string;
  name: string;
}

export interface AddLaneOperation {
  op: 'add_lane';
  key?: string;
  name: string;
  /** The new lane goes above this one; at the bottom when absent. */
  before_lane?: string;
}

export interface RenameLaneOperation {
  op: 'rename_lane';
  lane: string;
  name: string;
}

export interface RemoveLaneOperation {
  op: 'remove_lane';
  lane: string;
}

export interface AddFlowOperation {
  op: 'add_flow';
  key?: string;
  from: string;
  to: string;
  label?: string | null;
  is_exception?: boolean;
}

export interface RemoveFlowOperation {
  op: 'remove_flow';
  flow: string;
}

export interface RelabelFlowOperation {
  op: 'relabel_flow';
  flow: string;
  /** Null or blank removes the label. */
  label: string | null;
}

export interface LinkEvidenceOperation {
  op: 'link_evidence';
  step: string;
  evidence_id: string;
}

export interface UnlinkEvidenceOperation {
  op: 'unlink_evidence';
  step: string;
  evidence_id: string;
}

/** Shows a confirmed pain point on a step (`process_map_step_pain_points`). */
export interface LinkPainPointOperation {
  op: 'link_pain_point';
  step: string;
  pain_point_id: string;
}

export interface UnlinkPainPointOperation {
  op: 'unlink_pain_point';
  step: string;
  pain_point_id: string;
}

/** Cite a knowledge item, by its number (`VK-00412`), on a future step. */
export interface LinkVendorKnowledgeOperation {
  op: 'link_vendor_knowledge';
  step: string;
  item: string;
}

export interface UnlinkVendorKnowledgeOperation {
  op: 'unlink_vendor_knowledge';
  step: string;
  item: string;
}

/** `true` marks a step an assumption and unlinks its knowledge items; `false` clears the mark. */
export interface SetAssumptionOperation {
  op: 'set_assumption';
  step: string;
  value: boolean;
}

export type ChangeOperation =
  | AddStepOperation
  | MoveStepOperation
  | RemoveStepOperation
  | RenameStepOperation
  | AddLaneOperation
  | RenameLaneOperation
  | RemoveLaneOperation
  | AddFlowOperation
  | RemoveFlowOperation
  | RelabelFlowOperation
  | LinkEvidenceOperation
  | UnlinkEvidenceOperation
  | LinkPainPointOperation
  | UnlinkPainPointOperation
  | LinkVendorKnowledgeOperation
  | UnlinkVendorKnowledgeOperation
  | SetAssumptionOperation;

export type ChangeOrigin = 'chat' | 'manual' | 'canvas';

export interface EvidenceForLink {
  number: string | null;
  status: EvidenceStatus;
}

/** A pain point as `link_pain_point` sees it: its number and its status now. */
export interface PainPointForLink {
  number: string | null;
  status: 'proposed' | 'confirmed' | 'dismissed';
}

/** A knowledge item a change set names, as it is now. */
export interface KnowledgeItemForLink {
  id: string;
  number: string;
  kind: KnowledgeItemKind;
  status: KnowledgeItemStatus;
  /** Of the map's product, or a company item of its vendor (rule 10). */
  of_product: boolean;
  answer_code: string | null;
  library_item_id: string | null;
}

/** What a change to a future-state map is checked against (ADR-020 §3, §4). */
export interface FutureChangeContext {
  /** The validated current-state version the map was drawn against: change tags come from it. */
  current: MapModel;
  /**
   * The items `link_vendor_knowledge` may name, by number. For chat, only the
   * items code put in the prompt; for a hand edit, the items the request names.
   */
  items: ReadonlyMap<string, KnowledgeItemForLink>;
}

export interface ChangeContext {
  origin: ChangeOrigin;
  /** Who proposes or applies the change. New steps record it (rule 27). */
  user_id: string | null;
  /** The chat message that asked for the change (chat only). */
  message_id?: string | null;
  /** The items `link_evidence` names, by id, with their number and status now. */
  evidence?: ReadonlyMap<string, EvidenceForLink>;
  /** The pain points `link_pain_point` names, by id, with their number and status now. */
  pain_points?: ReadonlyMap<string, PainPointForLink>;
  /** Set for a future-state map, and only then (E13). */
  future?: FutureChangeContext;
}

/** What a change set touches, by key, for highlighting it before it is applied. */
export interface ChangePreview {
  added: string[];
  removed: string[];
  renamed: string[];
  /** Steps whose lane changed, or that a move placed after another step. */
  moved: string[];
  /** Flows added, removed, re-pointed or relabelled. */
  flows_changed: string[];
  /** Lanes added, removed or renamed. */
  lanes_changed: string[];
  /**
   * Steps whose citations changed: evidence, knowledge items or pain points
   * linked or unlinked, a client validation dropped, an assumption marked or
   * cleared.
   */
  citations_changed: string[];
}

export const CHANGE_SET_CHECKS = [
  'empty_change_set',
  'too_many_operations',
  'invalid_operation',
  'not_allowed',
  'unknown_step',
  'unknown_flow',
  'unknown_evidence',
  'unknown_citation',
  'lane_not_empty',
  'ambiguous_reorder',
  'evidence_not_confirmed',
  'unknown_pain_point',
  'pain_point_not_confirmed',
  'duplicate_pain_point',
  'no_change',
  // Map chat only: a name or label the model proposed holds a number the
  // person did not write and the map does not have (rule 22).
  'invented_number',
  // Future-state maps (E13).
  'future_only',
  'redraft_out_of_scope',
] as const;
export type ChangeSetCheck = MapCheck | FutureMapCheck | (typeof CHANGE_SET_CHECKS)[number];

export interface ChangeSetRefusal {
  valid: false;
  /**
   * The operation refused. For a check on the map the set leaves, the
   * operation from which the problem lasts to the end. Null when no single
   * operation is at fault (an empty set, or one that changes nothing).
   */
  operation_index: number | null;
  operation: ChangeOperationKind | null;
  check: ChangeSetCheck;
  message: string;
}

export interface AppliedChangeSet {
  valid: true;
  model: MapModel;
  /** The operations as applied: text cleaned and keys assigned to new lanes, steps and flows. */
  operations: ChangeOperation[];
  preview: ChangePreview;
  /**
   * One plain sentence per operation, then the steps marked assumptions and
   * the evidence the map would no longer cite.
   */
  summary: string[];
}

export type ChangeSetResult = AppliedChangeSet | ChangeSetRefusal;
export type ChangeSetValidation = Omit<AppliedChangeSet, 'model'> | ChangeSetRefusal;

/** Checks a change set against a map without keeping the result (E12-F09 §2). */
export function validateChangeSet(
  model: MapModel,
  operations: readonly ChangeOperation[],
  context: ChangeContext,
): ChangeSetValidation {
  const result = applyChangeSet(model, operations, context);
  if (!result.valid) return result;
  return {
    valid: true,
    operations: result.operations,
    preview: result.preview,
    summary: result.summary,
  };
}

/**
 * Applies the operations in order to a copy of `model` (never to `model`
 * itself) and checks the map they leave. Deterministic: the same map and
 * operations always give the same result, keys included.
 *
 * - `add_step` gives the new step the context's origin, user and message and
 *   no citations, so it is unconfirmed until evidence is linked (rule 27).
 *   With `after_step` it is inserted on that step's single outgoing flow.
 * - `move_step` with `after_step` re-wires: the step's predecessor connects to
 *   its successor, and the step is inserted between `after_step` and that
 *   step's successor. It is refused when the step or `after_step` has more
 *   than one incoming or outgoing flow, so a branch is never re-wired by
 *   guesswork (ADR-017 §4). Moving to another lane drops the step's
 *   client-validation citations, as does renaming it (ADR-017 §1).
 * - `remove_step` removes the step and its flows and connects the steps
 *   before it straight to the steps after it (E12-F09 scenario 13).
 * - `link_evidence`, `unlink_evidence`, `link_pain_point` and
 *   `unlink_pain_point` are refused unless the origin is `manual`; a new
 *   link must be to confirmed evidence, or to a confirmed pain point.
 *   A future-state step cites knowledge items instead, so `link_evidence` is
 *   refused there.
 * - `link_vendor_knowledge`, `unlink_vendor_knowledge` and `set_assumption`
 *   are refused unless `context.future` is set (a future-state map). A new
 *   link must name an item in `context.future.items` that is confirmed and
 *   of the map's product or its vendor (rule 10); linking clears the step's
 *   assumption mark; `set_assumption(true)` unlinks the step's items.
 * - On a future-state map, after the operations, every task step that is
 *   new, changed or automated against `context.future.current` and cites no
 *   knowledge item is marked an assumption, and the summary says so (rule
 *   24). Clearing the mark of such a step in the same set is refused.
 */
export function applyChangeSet(
  model: MapModel,
  operations: readonly ChangeOperation[],
  context: ChangeContext,
): ChangeSetResult {
  if (!Array.isArray(operations) || operations.length === 0)
    return refuse(null, null, 'empty_change_set', 'The change set has no operations.');
  const run = applyEach(model, operations, context);
  if (!('work' in run)) return run;
  const { work, applied, summary, moved, issuesAfter } = run;

  const first = mapIssues(work)[0];
  if (first) {
    let at = operations.length - 1;
    while (at > 0 && issuesAfter[at - 1]!.has(issueId(first))) at -= 1;
    return refuse(at, applied[at]!.op, first.check, first.message);
  }
  if (JSON.stringify(work) === JSON.stringify(model))
    return refuse(null, null, 'no_change', 'The change set leaves the map as it is.');

  const uncited = evidenceNoLongerCited(model, work);
  if (uncited.length > 0)
    summary.push(`After this change the map no longer cites ${joinWords(uncited)}.`);
  return {
    valid: true,
    model: work,
    operations: applied,
    preview: previewChange(model, work, moved),
    summary,
  };
}

/** The canvas's edits so far: the map they leave and what it still needs before it can be saved. */
export interface DraftedChangeSet {
  valid: true;
  model: MapModel;
  /** The operations as applied, keys assigned: the batch to send as one change set. */
  operations: ChangeOperation[];
  /** The checks on a whole map that the map still fails, in `mapIssues` order. */
  issues: MapIssue[];
}

/**
 * The canvas's edit mode (ADR-017 §6, Decision 54.32): applies the operations
 * like `applyChangeSet` and keeps the map they leave even when it does not
 * yet pass the checks on a whole map, listing what it still needs. While the
 * consultant is still drawing, a step just added may not be connected yet.
 * Each operation is still checked on its own (what it names exists, a new key
 * is free, a branch is never re-ordered). Nothing here reaches a map: the
 * edits are sent as one change set, checked in full by `validateChangeSet`,
 * and change the map only when applied. An empty list leaves the map as it is.
 */
export function draftChangeSet(
  model: MapModel,
  operations: readonly ChangeOperation[],
  context: ChangeContext,
): DraftedChangeSet | ChangeSetRefusal {
  if (!Array.isArray(operations))
    return refuse(null, null, 'invalid_operation', 'The change set is not a list of operations.');
  if (operations.length === 0)
    return { valid: true, model: cloneModel(model), operations: [], issues: mapIssues(model) };
  const run = applyEach(model, operations, context);
  if (!('work' in run)) return run;
  return { valid: true, model: run.work, operations: run.applied, issues: mapIssues(run.work) };
}

/** Applies each operation in order to a copy of `model`, stopping at the first one refused. */
function applyEach(
  model: MapModel,
  operations: readonly ChangeOperation[],
  context: ChangeContext,
):
  | {
      work: MapModel;
      applied: ChangeOperation[];
      summary: string[];
      moved: Set<string>;
      issuesAfter: Set<string>[];
    }
  | ChangeSetRefusal {
  if (operations.length > MAX_OPERATIONS)
    return refuse(
      null,
      null,
      'too_many_operations',
      `A change set holds at most ${MAX_OPERATIONS} operations.`,
    );

  const work = cloneModel(model);
  const applied: ChangeOperation[] = [];
  const summary: string[] = [];
  const moved = new Set<string>();
  const issuesAfter: Set<string>[] = [];
  /** Steps whose assumption mark an operation cleared, with that operation's index. */
  const unmarked = new Map<string, number>();

  for (let i = 0; i < operations.length; i += 1) {
    const raw: unknown = operations[i];
    const kind = operationKind(raw);
    if (!kind)
      return refuse(i, null, 'invalid_operation', `Operation ${i + 1} is not a map operation.`);
    if (MANUAL_ONLY_OPERATIONS.includes(kind) && context.origin !== 'manual')
      return refuse(
        i,
        kind,
        'not_allowed',
        kind === 'link_pain_point' || kind === 'unlink_pain_point'
          ? 'Pain points are linked to a step and unlinked from it in the step panel only.'
          : 'Evidence is linked to a step and unlinked from it in the step panel only.',
      );
    if (FUTURE_OPERATIONS.includes(kind) && !context.future)
      return refuse(
        i,
        kind,
        'future_only',
        'Vendor evidence and assumptions are for future-state maps only.',
      );
    if (kind === 'link_evidence' && context.future)
      return refuse(
        i,
        kind,
        'not_allowed',
        'A future-state step cites vendor knowledge items, not discovery evidence.',
      );
    const outcome = APPLY[kind](work, raw as Record<string, unknown>, context);
    if ('check' in outcome) return refuse(i, kind, outcome.check, outcome.message);
    applied.push(outcome.op);
    summary.push(outcome.summary);
    if (outcome.moved) moved.add(outcome.moved);
    if (outcome.unmarked !== undefined) unmarked.set(outcome.unmarked, i);
    issuesAfter.push(new Set(mapIssues(work).map(issueId)));
  }

  if (context.future) {
    const steps = stepsByKey(work);
    for (const key of markAssumptions(work, context.future.current)) {
      const at = unmarked.get(key);
      if (at !== undefined)
        return refuse(
          at,
          applied[at]!.op,
          'assumption_required',
          `${stepLabel(steps.get(key)!)} says the product does something and cites no vendor evidence, so it stays an assumption: link a knowledge item to it instead.`,
        );
      summary.push(
        `${stepLabel(steps.get(key)!)} is marked an assumption: it cites no vendor evidence.`,
      );
    }
  }

  return { work, applied, summary, moved, issuesAfter };
}

/** What changed between two versions of a map, by key. */
export function previewChange(
  before: MapModel,
  after: MapModel,
  moved: ReadonlySet<string> = new Set(),
): ChangePreview {
  const was = stepsByKey(before);
  const now = stepsByKey(after);
  const kept = after.steps.filter((s) => was.has(s.key));
  const citationIds = (s: MapStep) =>
    [
      ...s.citations.map(citedId),
      ...s.pain_points.map((p) => `pp:${p.pain_point_id}`),
      ...(s.is_assumption === true ? ['assumption'] : []),
    ].join(',');

  const flowsBefore = new Map(before.flows.map((f) => [f.key, f]));
  const flowsAfter = new Map(after.flows.map((f) => [f.key, f]));
  const sameFlow = (a: MapFlow, b: MapFlow) =>
    a.from === b.from && a.to === b.to && a.label === b.label && a.is_exception === b.is_exception;

  const lanesBefore = new Map(before.lanes.map((l) => [l.key, l]));
  const lanesAfter = new Map(after.lanes.map((l) => [l.key, l]));

  return {
    added: after.steps.filter((s) => !was.has(s.key)).map((s) => s.key),
    removed: before.steps.filter((s) => !now.has(s.key)).map((s) => s.key),
    renamed: kept.filter((s) => was.get(s.key)!.name !== s.name).map((s) => s.key),
    moved: kept
      .filter((s) => was.get(s.key)!.lane !== s.lane || moved.has(s.key))
      .map((s) => s.key),
    flows_changed: [
      ...after.flows.filter((f) => {
        const old = flowsBefore.get(f.key);
        return !old || !sameFlow(old, f);
      }),
      ...before.flows.filter((f) => !flowsAfter.has(f.key)),
    ].map((f) => f.key),
    lanes_changed: [
      ...after.lanes.filter((l) => lanesBefore.get(l.key)?.name !== l.name),
      ...before.lanes.filter((l) => !lanesAfter.has(l.key)),
    ].map((l) => l.key),
    citations_changed: kept
      .filter((s) => citationIds(was.get(s.key)!) !== citationIds(s))
      .map((s) => s.key),
  };
}

/**
 * What changed between two versions, in plain words, from the maps alone
 * (no operations to replay): for the version history and for undo. A step
 * re-ordered in its lane shows as changed flows.
 */
export function describeChange(before: MapModel, after: MapModel): string[] {
  const p = previewChange(before, after);
  const was = stepsByKey(before);
  const now = stepsByKey(after);
  const lanesBefore = new Map(before.lanes.map((l) => [l.key, l]));
  const lanesAfter = new Map(after.lanes.map((l) => [l.key, l]));
  const steps = (keys: string[], from: Map<string, MapStep>) =>
    joinWords(keys.map((k) => stepLabel(from.get(k)!)));
  const lines: string[] = [];
  if (p.added.length > 0) lines.push(`Added ${steps(p.added, now)}.`);
  if (p.removed.length > 0) lines.push(`Removed ${steps(p.removed, was)}.`);
  for (const k of p.renamed)
    lines.push(`Renamed ${stepLabel(was.get(k)!)} to ${stepLabel(now.get(k)!)}.`);
  if (p.moved.length > 0) lines.push(`Moved ${steps(p.moved, now)} to another lane.`);
  for (const k of p.lanes_changed) {
    const old = lanesBefore.get(k);
    const lane = lanesAfter.get(k);
    if (!old) lines.push(`Added the lane ${quote(lane!.name)}.`);
    else if (!lane) lines.push(`Removed the lane ${quote(old.name)}.`);
    else lines.push(`Renamed the lane ${quote(old.name)} to ${quote(lane.name)}.`);
  }
  if (p.flows_changed.length > 0) lines.push(`Changed ${plural(p.flows_changed.length, 'flow')}.`);
  if (p.citations_changed.length > 0)
    lines.push(
      `Changed what ${steps(p.citations_changed, now)} cite${p.citations_changed.length === 1 ? 's' : ''}.`,
    );
  const uncited = evidenceNoLongerCited(before, after);
  if (uncited.length > 0) lines.push(`The map no longer cites ${joinWords(uncited)}.`);
  return lines;
}

// ---------------------------------------------------------------------------

type Failure = { check: ChangeSetCheck; message: string };
type Success = { op: ChangeOperation; summary: string; moved?: string; unmarked?: string };
type Outcome = Failure | Success;
type Raw = Record<string, unknown>;

const APPLY: Record<
  ChangeOperationKind,
  (m: MapModel, op: Raw, context: ChangeContext) => Outcome
> = {
  add_step: addStep,
  move_step: moveStep,
  remove_step: removeStep,
  rename_step: renameStep,
  add_lane: addLane,
  rename_lane: renameLane,
  remove_lane: removeLane,
  add_flow: addFlow,
  remove_flow: removeFlow,
  relabel_flow: relabelFlow,
  link_evidence: linkEvidence,
  unlink_evidence: unlinkEvidence,
  link_pain_point: linkPainPoint,
  unlink_pain_point: unlinkPainPoint,
  link_vendor_knowledge: linkVendorKnowledge,
  unlink_vendor_knowledge: unlinkVendorKnowledge,
  set_assumption: setAssumption,
};

function addStep(m: MapModel, op: Raw, context: ChangeContext): Outcome {
  if (!isStepType(op.type))
    return fail('invalid_step_type', `${quote(String(op.type))} is not a step type.`);
  const lane = findLane(m, op.lane);
  if ('check' in lane) return lane;
  const name = cleanName(op.name, MAX_NAME_LENGTH);
  if (name === null) return fail('invalid_name', nameRule('A step'));
  const system = cleanOptional(op.system, MAX_NAME_LENGTH);
  if (system === undefined) return fail('invalid_name', nameRule("A step's system"));
  const key = newKey(op.key, 's', m.steps);
  if (typeof key !== 'string') return key;
  let after: MapStep | null = null;
  if (op.after_step !== undefined) {
    const found = findStep(m, op.after_step);
    if ('check' in found) return found;
    if (m.flows.filter((f) => f.from === found.key).length > 1)
      return fail(
        'ambiguous_reorder',
        `${stepLabel(found)} has more than one outgoing flow, so the new step cannot go after it; connect it with flows instead.`,
      );
    after = found;
  }
  m.steps.push({
    key,
    type: op.type,
    lane: lane.key,
    name,
    system,
    origin: context.origin,
    origin_user_id: context.user_id,
    origin_message_id: context.origin === 'chat' ? (context.message_id ?? null) : null,
    citations: [],
    pain_points: [],
  });
  if (after) insertAfter(m, key, after.key);
  return {
    op: {
      op: 'add_step',
      key,
      type: op.type,
      lane: lane.key,
      name,
      system,
      ...(after ? { after_step: after.key } : {}),
    },
    summary: `Add ${quote(name)}${after ? ` after ${stepLabel(after)}` : ''}, in ${quote(lane.name)}.`,
  };
}

function moveStep(m: MapModel, op: Raw): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  if (op.lane === undefined && op.after_step === undefined)
    return fail('invalid_operation', 'Moving a step needs a lane, a step to follow, or both.');
  const fromLane = laneName(m, step.lane);
  let laneChanged = false;
  if (op.lane !== undefined) {
    const lane = findLane(m, op.lane);
    if ('check' in lane) return lane;
    if (lane.key !== step.lane) {
      step.lane = lane.key;
      dropValidationCitations(step);
      laneChanged = true;
    }
  }
  let after: MapStep | null = null;
  let reordered = false;
  if (op.after_step !== undefined) {
    if (op.after_step === step.key)
      return fail('invalid_operation', `${stepLabel(step)} cannot follow itself.`);
    const found = findStep(m, op.after_step);
    if ('check' in found) return found;
    after = found;
    const ins = m.flows.filter((f) => f.to === step.key);
    const outs = m.flows.filter((f) => f.from === step.key);
    if (ins.length > 1 || outs.length > 1) return onBranch(step);
    if (!(ins.length === 1 && ins[0]!.from === after.key)) {
      detach(m, ins[0], outs[0]);
      const afterIns = m.flows.filter((f) => f.to === found.key).length;
      const afterOuts = m.flows.filter((f) => f.from === found.key).length;
      if (afterIns > 1 || afterOuts > 1) return onBranch(after);
      insertAfter(m, step.key, after.key);
      reordered = true;
    }
  }
  const parts: string[] = [];
  if (laneChanged) parts.push(`from ${quote(fromLane)} to ${quote(laneName(m, step.lane))}`);
  if (reordered && after) parts.push(`to follow ${stepLabel(after)}`);
  return {
    op: {
      op: 'move_step',
      step: step.key,
      ...(op.lane !== undefined ? { lane: step.lane } : {}),
      ...(after ? { after_step: after.key } : {}),
    },
    summary:
      parts.length > 0
        ? `Move ${stepLabel(step)} ${parts.join(', ')}.`
        : `Keep ${stepLabel(step)} where it is.`,
    ...(laneChanged || reordered ? { moved: step.key } : {}),
  };
}

function removeStep(m: MapModel, op: Raw): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  const ins = m.flows.filter((f) => f.to === step.key);
  const outs = m.flows.filter((f) => f.from === step.key);
  m.flows = m.flows.filter((f) => f.from !== step.key && f.to !== step.key);
  m.steps = m.steps.filter((s) => s !== step);
  // Keys of the removed flows stay reserved here, so each incoming flow can keep its own.
  const taken = new Set([...m.flows.map((f) => f.key), ...ins.map((f) => f.key)]);
  let rewired = false;
  for (const fin of ins) {
    let key: string | null = fin.key;
    for (const fout of outs) {
      if (fin.from === fout.to) continue;
      if (m.flows.some((f) => f.from === fin.from && f.to === fout.to)) continue;
      const flowKey = key ?? nextFreeKey('f', taken);
      taken.add(flowKey);
      key = null;
      m.flows.push({
        key: flowKey,
        from: fin.from,
        to: fout.to,
        label: fin.label,
        is_exception: fin.is_exception,
      });
      rewired = true;
    }
  }
  return {
    op: { op: 'remove_step', step: step.key },
    summary: rewired
      ? `Remove ${stepLabel(step)} and connect the steps before it straight to the steps after it.`
      : `Remove ${stepLabel(step)} and its flows.`,
  };
}

function renameStep(m: MapModel, op: Raw): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  const name = cleanName(op.name, MAX_NAME_LENGTH);
  if (name === null) return fail('invalid_name', nameRule('A step'));
  const old = stepLabel(step);
  if (name !== step.name) {
    step.name = name;
    dropValidationCitations(step);
  }
  return {
    op: { op: 'rename_step', step: step.key, name },
    summary: `Rename ${old} to ${quote(name)}.`,
  };
}

function addLane(m: MapModel, op: Raw): Outcome {
  const name = cleanName(op.name, MAX_NAME_LENGTH);
  if (name === null) return fail('invalid_name', nameRule('A lane'));
  const key = newKey(op.key, 'l', m.lanes);
  if (typeof key !== 'string') return key;
  let at = m.lanes.length;
  let before: MapLane | null = null;
  if (op.before_lane !== undefined) {
    const found = findLane(m, op.before_lane);
    if ('check' in found) return found;
    before = found;
    at = m.lanes.indexOf(found);
  }
  m.lanes.splice(at, 0, { key, name });
  return {
    op: { op: 'add_lane', key, name, ...(before ? { before_lane: before.key } : {}) },
    summary: `Add the lane ${quote(name)}${before ? ` above ${quote(before.name)}` : ''}.`,
  };
}

function renameLane(m: MapModel, op: Raw): Outcome {
  const lane = findLane(m, op.lane);
  if ('check' in lane) return lane;
  const name = cleanName(op.name, MAX_NAME_LENGTH);
  if (name === null) return fail('invalid_name', nameRule('A lane'));
  const old = lane.name;
  lane.name = name;
  return {
    op: { op: 'rename_lane', lane: lane.key, name },
    summary: `Rename the lane ${quote(old)} to ${quote(name)}.`,
  };
}

function removeLane(m: MapModel, op: Raw): Outcome {
  const lane = findLane(m, op.lane);
  if ('check' in lane) return lane;
  const held = m.steps.filter((s) => s.lane === lane.key).length;
  if (held > 0)
    return fail(
      'lane_not_empty',
      `The lane ${quote(lane.name)} still holds ${plural(held, 'step')}; move or remove ${held === 1 ? 'it' : 'them'} first.`,
    );
  m.lanes = m.lanes.filter((l) => l !== lane);
  return {
    op: { op: 'remove_lane', lane: lane.key },
    summary: `Remove the lane ${quote(lane.name)}.`,
  };
}

function addFlow(m: MapModel, op: Raw): Outcome {
  const from = m.steps.find((s) => s.key === op.from);
  const to = m.steps.find((s) => s.key === op.to);
  if (!from || !to) {
    const missing = from ? op.to : op.from;
    return fail(
      'flow_to_missing_step',
      `A flow would point to a step that is not on the map (${quote(String(missing))}).`,
    );
  }
  if (from === to)
    return fail('self_flow', `A flow cannot lead from ${stepLabel(from)} back to itself.`);
  if (m.flows.some((f) => f.from === from.key && f.to === to.key))
    return fail(
      'duplicate_flow',
      `There is already a flow from ${stepLabel(from)} to ${stepLabel(to)}.`,
    );
  const label = cleanOptional(op.label, MAX_LABEL_LENGTH);
  if (label === undefined) return fail('invalid_name', labelRule());
  if (op.is_exception !== undefined && typeof op.is_exception !== 'boolean')
    return fail('invalid_operation', 'is_exception is true or false.');
  const key = newKey(op.key, 'f', m.flows);
  if (typeof key !== 'string') return key;
  const isException = op.is_exception === true;
  m.flows.push({ key, from: from.key, to: to.key, label, is_exception: isException });
  return {
    op: { op: 'add_flow', key, from: from.key, to: to.key, label, is_exception: isException },
    summary: `Connect ${stepLabel(from)} to ${stepLabel(to)}${label ? `, labelled ${quote(label)}` : ''}${isException ? ', as an exception' : ''}.`,
  };
}

function removeFlow(m: MapModel, op: Raw): Outcome {
  const flow = findFlow(m, op.flow);
  if ('check' in flow) return flow;
  m.flows = m.flows.filter((f) => f !== flow);
  return {
    op: { op: 'remove_flow', flow: flow.key },
    summary: `Remove the flow from ${flowEnds(m, flow)}. Both steps stay.`,
  };
}

function relabelFlow(m: MapModel, op: Raw): Outcome {
  const flow = findFlow(m, op.flow);
  if ('check' in flow) return flow;
  const label = cleanOptional(op.label, MAX_LABEL_LENGTH);
  if (label === undefined) return fail('invalid_name', labelRule());
  flow.label = label;
  return {
    op: { op: 'relabel_flow', flow: flow.key, label },
    summary: label
      ? `Label the flow from ${flowEnds(m, flow)} ${quote(label)}.`
      : `Remove the label of the flow from ${flowEnds(m, flow)}.`,
  };
}

function linkEvidence(m: MapModel, op: Raw, context: ChangeContext): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  if (typeof op.evidence_id !== 'string')
    return fail('invalid_operation', 'Linking evidence needs an evidence item.');
  const evidence = context.evidence?.get(op.evidence_id);
  if (!evidence) return fail('unknown_evidence', 'That evidence item is not in this engagement.');
  const name = evidence.number ?? 'That evidence item';
  if (evidence.status !== 'confirmed')
    return fail(
      'evidence_not_confirmed',
      `${name} is ${evidence.status}; a step can cite confirmed evidence only.`,
    );
  if (
    step.citations.some((c) => c.kind === 'discovery_evidence' && c.evidence_id === op.evidence_id)
  )
    return fail('duplicate_citation', `${stepLabel(step)} already cites ${name}.`);
  step.citations.push({
    kind: 'discovery_evidence',
    evidence_id: op.evidence_id,
    evidence_number: evidence.number,
    evidence_status: evidence.status,
  });
  return {
    op: { op: 'link_evidence', step: step.key, evidence_id: op.evidence_id },
    summary: `Link ${name} to ${stepLabel(step)}.`,
  };
}

function unlinkEvidence(m: MapModel, op: Raw): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  const at = step.citations.findIndex(
    (c) => c.kind === 'discovery_evidence' && c.evidence_id === op.evidence_id,
  );
  const cited = step.citations[at];
  if (!cited || cited.kind !== 'discovery_evidence')
    return fail('unknown_citation', `${stepLabel(step)} does not cite that evidence item.`);
  step.citations.splice(at, 1);
  return {
    op: { op: 'unlink_evidence', step: step.key, evidence_id: cited.evidence_id },
    summary: `Unlink ${cited.evidence_number ?? 'the evidence item'} from ${stepLabel(step)}.`,
  };
}

function linkPainPoint(m: MapModel, op: Raw, context: ChangeContext): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  if (typeof op.pain_point_id !== 'string')
    return fail('invalid_operation', 'Linking a pain point needs a pain point.');
  const painPoint = context.pain_points?.get(op.pain_point_id);
  if (!painPoint) return fail('unknown_pain_point', 'That pain point is not in this engagement.');
  const name = painPoint.number ?? 'That pain point';
  if (painPoint.status !== 'confirmed')
    return fail(
      'pain_point_not_confirmed',
      `${name} is ${painPoint.status}; a step shows confirmed pain points only.`,
    );
  if (step.pain_points.some((p) => p.pain_point_id === op.pain_point_id))
    return fail('duplicate_pain_point', `${stepLabel(step)} already shows ${name}.`);
  step.pain_points.push({ pain_point_id: op.pain_point_id, number: painPoint.number });
  return {
    op: { op: 'link_pain_point', step: step.key, pain_point_id: op.pain_point_id },
    summary: `Show ${name} on ${stepLabel(step)}.`,
  };
}

function unlinkPainPoint(m: MapModel, op: Raw): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  const at = step.pain_points.findIndex((p) => p.pain_point_id === op.pain_point_id);
  const shown = step.pain_points[at];
  if (!shown)
    return fail('unknown_pain_point', `${stepLabel(step)} does not show that pain point.`);
  step.pain_points.splice(at, 1);
  return {
    op: { op: 'unlink_pain_point', step: step.key, pain_point_id: shown.pain_point_id },
    summary: `Take ${shown.number ?? 'the pain point'} off ${stepLabel(step)}.`,
  };
}

function linkVendorKnowledge(m: MapModel, op: Raw, context: ChangeContext): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  const number = knowledgeNumber(op.item);
  if (!number)
    return fail(
      'invalid_operation',
      'Linking vendor evidence needs an item number, such as VK-00412.',
    );
  const item = context.future?.items.get(number);
  if (!item)
    return fail(
      'knowledge_item_not_allowed',
      `${number} is not one of this product's knowledge items that can be cited here.`,
    );
  if (item.status !== 'confirmed')
    return fail(
      'knowledge_item_not_allowed',
      `${number} is ${item.status}; a step cites confirmed knowledge items only.`,
    );
  if (!item.of_product)
    return fail(
      'knowledge_item_not_allowed',
      `${number} is about another product; a step cites this product's items or its vendor's company items.`,
    );
  if (knowledgeCitations(step).some((c) => c.item_id === item.id))
    return fail('duplicate_citation', `${stepLabel(step)} already cites ${number}.`);
  step.citations.push({
    kind: 'vendor_knowledge',
    item_id: item.id,
    item_number: item.number,
    item_kind: item.kind,
    item_status: item.status,
    answer_code: item.answer_code,
    library_item_id: item.library_item_id,
  });
  const wasAssumption = step.is_assumption === true;
  delete step.is_assumption;
  return {
    op: { op: 'link_vendor_knowledge', step: step.key, item: number },
    summary: `Link ${number} to ${stepLabel(step)}${wasAssumption ? ', which is no longer an assumption' : ''}.`,
  };
}

function unlinkVendorKnowledge(m: MapModel, op: Raw): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  const number = knowledgeNumber(op.item);
  const at = step.citations.findIndex(
    (c) => c.kind === 'vendor_knowledge' && number !== null && c.item_number === number,
  );
  if (at < 0)
    return fail(
      'unknown_citation',
      `${stepLabel(step)} does not cite ${number ?? quote(String(op.item))}.`,
    );
  step.citations.splice(at, 1);
  return {
    op: { op: 'unlink_vendor_knowledge', step: step.key, item: number! },
    summary: `Unlink ${number} from ${stepLabel(step)}.`,
  };
}

function setAssumption(m: MapModel, op: Raw): Outcome {
  const step = findStep(m, op.step);
  if ('check' in step) return step;
  if (typeof op.value !== 'boolean')
    return fail('invalid_operation', 'An assumption is set to true or false.');
  if (!op.value) {
    delete step.is_assumption;
    return {
      op: { op: 'set_assumption', step: step.key, value: false },
      summary: `${stepLabel(step)} is no longer an assumption.`,
      unmarked: step.key,
    };
  }
  if (!isTaskType(step.type))
    return fail(
      'invalid_operation',
      `${stepLabel(step)} is not a step that does work, so it is not an assumption.`,
    );
  const unlinked = knowledgeCitations(step).map((c) => c.item_number ?? 'an item');
  step.citations = step.citations.filter((c) => c.kind !== 'vendor_knowledge');
  step.is_assumption = true;
  return {
    op: { op: 'set_assumption', step: step.key, value: true },
    summary: `Mark ${stepLabel(step)} an assumption${unlinked.length > 0 ? ` and unlink ${joinWords(unlinked)}` : ''}.`,
  };
}

// ---------------------------------------------------------------------------

/**
 * A redraft change set may touch only the steps that cite items with newer
 * vendor information (ADR-020 §4, §6; rule 25). Every step an operation names
 * (its step, the step it goes after, a flow's ends) must be affected or added
 * by the set itself; lanes other steps share are not renamed or removed.
 * `model` is the base version, for the ends of flows named by key.
 */
export function redraftScope(
  operations: readonly ChangeOperation[],
  affectedKeys: readonly string[],
  model: MapModel,
): { valid: true } | ChangeSetRefusal {
  const inScope = new Set(affectedKeys);
  const flowEnds = new Map(model.flows.map((f) => [f.key, [f.from, f.to]]));
  for (let i = 0; i < operations.length; i += 1) {
    const op = operations[i] as unknown as Raw;
    const kind = operationKind(op);
    if (!kind)
      return refuse(i, null, 'invalid_operation', `Operation ${i + 1} is not a map operation.`);
    const named: unknown[] = [];
    switch (kind) {
      case 'add_lane':
        break;
      case 'rename_lane':
      case 'remove_lane':
        return refuse(
          i,
          kind,
          'redraft_out_of_scope',
          'A redraft changes only the steps with newer vendor information, not the lanes other steps are in.',
        );
      case 'add_step':
        if (op.after_step !== undefined) named.push(op.after_step);
        break;
      case 'move_step':
        named.push(op.step, ...(op.after_step !== undefined ? [op.after_step] : []));
        break;
      case 'add_flow':
        named.push(op.from, op.to);
        break;
      case 'remove_flow':
      case 'relabel_flow':
        named.push(...(flowEnds.get(String(op.flow)) ?? [op.flow]));
        break;
      default:
        named.push(op.step);
    }
    const outside = named.find((k) => typeof k !== 'string' || !inScope.has(k));
    if (outside !== undefined)
      return refuse(
        i,
        kind,
        'redraft_out_of_scope',
        `A redraft changes only the steps with newer vendor information; ${quote(String(outside))} is not one of them.`,
      );
    if (kind === 'add_step' && typeof op.key === 'string') inScope.add(op.key);
    if (kind === 'add_flow' && typeof op.key === 'string')
      flowEnds.set(op.key, [String(op.from), String(op.to)]);
  }
  return { valid: true };
}

/** Puts `stepKey` on the single flow leaving `afterKey`, or after it when nothing leaves it. */
function insertAfter(m: MapModel, stepKey: string, afterKey: string): void {
  const taken = new Set(m.flows.map((f) => f.key));
  const out = m.flows.find((f) => f.from === afterKey);
  if (out) {
    const next = out.to;
    out.to = stepKey; // keeps its key, label and exception flag: it still leaves `afterKey`
    m.flows.push({
      key: nextFreeKey('f', taken),
      from: stepKey,
      to: next,
      label: null,
      is_exception: false,
    });
  } else {
    m.flows.push({
      key: nextFreeKey('f', taken),
      from: afterKey,
      to: stepKey,
      label: null,
      is_exception: false,
    });
  }
}

/** Takes a step with at most one flow in and one out off its path, joining its neighbours. */
function detach(m: MapModel, fin: MapFlow | undefined, fout: MapFlow | undefined): void {
  if (fout) m.flows = m.flows.filter((f) => f !== fout);
  if (!fin) return;
  const joined = fout ? fout.to : null;
  const joinable =
    joined !== null &&
    joined !== fin.from &&
    !m.flows.some((f) => f !== fin && f.from === fin.from && f.to === joined);
  if (joinable) fin.to = joined;
  else m.flows = m.flows.filter((f) => f !== fin);
}

function dropValidationCitations(step: MapStep): void {
  step.citations = step.citations.filter((c) => c.kind !== 'client_validation');
}

function evidenceNoLongerCited(before: MapModel, after: MapModel): string[] {
  const numbers = (m: MapModel) =>
    new Set(
      m.steps.flatMap((s) =>
        s.citations.flatMap((c) =>
          c.kind === 'discovery_evidence' && c.evidence_number ? [c.evidence_number] : [],
        ),
      ),
    );
  const still = numbers(after);
  return [...numbers(before)].filter((n) => !still.has(n)).sort(compareCodes);
}

function onBranch(step: MapStep): Failure {
  return fail(
    'ambiguous_reorder',
    `${stepLabel(step)} is on a branch (more than one flow in or out), so it cannot be re-ordered by moving; change its flows instead.`,
  );
}

function operationKind(raw: unknown): ChangeOperationKind | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const op = (raw as Raw).op;
  return typeof op === 'string' && (CHANGE_OPERATIONS as readonly string[]).includes(op)
    ? (op as ChangeOperationKind)
    : null;
}

function findStep(m: MapModel, key: unknown): MapStep | Failure {
  const step = typeof key === 'string' ? m.steps.find((s) => s.key === key) : undefined;
  return step ?? fail('unknown_step', `The step ${quote(String(key))} is not on the map.`);
}

function findLane(m: MapModel, key: unknown): MapLane | Failure {
  const lane = typeof key === 'string' ? m.lanes.find((l) => l.key === key) : undefined;
  return lane ?? fail('unknown_lane', `The lane ${quote(String(key))} does not exist.`);
}

function findFlow(m: MapModel, key: unknown): MapFlow | Failure {
  const flow = typeof key === 'string' ? m.flows.find((f) => f.key === key) : undefined;
  return flow ?? fail('unknown_flow', `The flow ${quote(String(key))} is not on the map.`);
}

/** The given key, checked, or the next free `<prefix><n>`. */
function newKey(given: unknown, prefix: string, existing: ReadonlyArray<{ key: string }>) {
  const taken = new Set(existing.map((x) => x.key));
  if (given === undefined) return nextFreeKey(prefix, taken);
  if (typeof given !== 'string' || !KEY_PATTERN.test(given))
    return fail(
      'invalid_key',
      `${quote(String(given))} is not a valid key: use up to 64 letters, digits, “_” or “-”.`,
    );
  if (taken.has(given)) return fail('key_collision', `The key ${quote(given)} is already in use.`);
  return given;
}

function cleanName(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const text = cleanText(value);
  return text !== '' && text.length <= max ? text : null;
}

/** Null for absent or blank; undefined when the value is not acceptable. */
function cleanOptional(value: unknown, max: number): string | null | undefined {
  if (value === undefined || value === null) return null;
  if (typeof value !== 'string') return undefined;
  const text = cleanText(value);
  if (text === '') return null;
  return text.length <= max ? text : undefined;
}

function nameRule(what: string): string {
  return `${what} needs a name of up to ${MAX_NAME_LENGTH} characters.`;
}

function labelRule(): string {
  return `A flow label has up to ${MAX_LABEL_LENGTH} characters.`;
}

function laneName(m: MapModel, key: string): string {
  return m.lanes.find((l) => l.key === key)?.name ?? key;
}

function flowEnds(m: MapModel, flow: MapFlow): string {
  const name = (key: string) => {
    const step = m.steps.find((s) => s.key === key);
    return step ? stepLabel(step) : quote(key);
  };
  return `${name(flow.from)} to ${name(flow.to)}`;
}

function joinWords(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function issueId(issue: MapIssue): string {
  return `${issue.check}:${issue.subject ?? ''}`;
}

function fail(check: ChangeSetCheck, message: string): Failure {
  return { check, message };
}

function refuse(
  operationIndex: number | null,
  operation: ChangeOperationKind | null,
  check: ChangeSetCheck,
  message: string,
): ChangeSetRefusal {
  return { valid: false, operation_index: operationIndex, operation, check, message };
}
